import COMMODITY_TOPICS from './commodityTopics';

const U = (id, w = 1600) => `https://images.unsplash.com/${id}?q=85&w=${w}`;

const CORE_TOPICS = {
  'excelsior': {
    title: 'Excelsior', category: 'The Xcelsior Essence', accent: '#00CED1',
    tagline: 'Latin: Ever Upward — our commitment to continuous rise',
    heroImage: U('photo-1486406146926-c627a92ad1ab'),
    overview: [
      "Excelsior — 'ever upward' — is the first pillar of our name and the engine of our culture. It is the refusal to accept Africa's current ceiling as its destiny, and the discipline to climb past it: project by project, megawatt by megawatt, harvest by harvest.",
      "In practice, Excelsior means compounding ambition. A 250,000-home solar program becomes a 1GW initiative. A veteran farming pilot becomes 12,750 productive plots. Every milestone is a base camp, never a summit."
    ],
    highlights: [
      { t: 'Relentless Progress', d: 'Every engagement designed to scale beyond its first phase' },
      { t: 'Transcending Limits', d: 'Rejecting artificial constraints on African industry' },
      { t: 'Compounding Ambition', d: 'Milestones treated as platforms, not finish lines' },
      { t: 'Cultural DNA', d: 'From the boardroom to the field, we climb' }
    ],
    stats: [
      { value: '2020', label: 'Founded amid a global crisis' },
      { value: '1GW', label: 'Solar ambition and counting' },
      { value: '∞', label: 'Ceiling we accept' }
    ],
    initiative: { title: 'Ascende Superius', text: "Our motto — 'Seek Higher Things' — captures Excelsior in two words. It closes every document we write and opens every project we start." },
    related: ['exousia', 'eccellenza', 'our-story']
  },
  'exousia': {
    title: 'Exousia', category: 'The Xcelsior Essence', accent: '#FF8C00',
    tagline: 'Greek: Authority & Power — our mandate to act with legitimacy',
    heroImage: U('photo-1521737604893-d14cc237f11d'),
    overview: [
      "Exousia is authority that is conferred, not seized — power exercised with legitimacy, responsibility and accountability. It defines how Xcelsior engages: through licensed channels, regulated markets, transparent governance and formal partnership with governments and communities.",
      "This is why our commodity chains are documented end-to-end, our funds are properly structured, and our projects align with national development strategies. Influence, for us, must always be earned and answerable."
    ],
    highlights: [
      { t: 'Legitimate Channels', d: 'Licensed, regulated and fully documented operations' },
      { t: 'Accountable Power', d: 'Governance structures that answer to stakeholders' },
      { t: 'Government Alignment', d: 'Projects mapped to Vision 2030 and Agenda 2063' },
      { t: 'Community Mandate', d: 'Social licence earned through shared prosperity' }
    ],
    stats: [
      { value: '100%', label: 'Licensed operations' },
      { value: '10', label: 'UN SDGs advanced' },
      { value: '2', label: 'National strategies served' }
    ],
    initiative: { title: 'Transformative Influence', text: 'From policy advisory to ministry-level frameworks, Exousia is our commitment to use every ounce of influence for continental transformation.' },
    related: ['excelsior', 'eccellenza', 'zimbabwe-vision-2030']
  },
  'eccellenza': {
    title: 'Eccellenza', category: 'The Xcelsior Essence', accent: '#00CED1',
    tagline: 'Italian: Excellence — superior standards in every deliverable',
    heroImage: U('photo-1573164713988-8665fc963095'),
    overview: [
      "Eccellenza is the standard we hold every deliverable to — whether it is a 100-page investment memorandum, a tonne of lithium concentrate or a solar-irrigated maize field. African excellence, delivered at global benchmarks, without apology or asterisk.",
      "It shows up in our ESG audits, our CBAM-ready value chains, our LBMA-benchmarked trading and our insistence that 'good enough for Africa' is a phrase that should not exist."
    ],
    highlights: [
      { t: 'Global Benchmarks', d: 'International standards applied to every output' },
      { t: 'ESG Gold Standard', d: 'Environmental and social rigour beyond compliance' },
      { t: 'Zero Compromise', d: 'Quality control at every link of every chain' },
      { t: 'Pride of Origin', d: 'African excellence, exported to the world' }
    ],
    stats: [
      { value: 'CBAM', label: 'EU-compliance-ready operations' },
      { value: 'ISO', label: 'Aligned management systems' },
      { value: '1', label: 'Standard: excellence' }
    ],
    initiative: { title: 'Service Excellence Value', text: 'Eccellenza powers our core value of Service Excellence — an uncompromising commitment to quality in every interaction.' },
    related: ['excelsior', 'exousia', 'esg-standard']
  },
  'our-story': {
    title: 'Our Story', category: 'The Movement', accent: '#FF8C00',
    tagline: 'Established during COVID-19 — a movement, not just a company',
    heroImage: U('photo-1524594081293-190a2fe0baae'),
    overview: [
      "Xcelsior was founded in 2020, in the depths of the COVID-19 crisis, when supply chains had collapsed and Africa's dependence on external systems was laid bare. Our founders chose that moment to build — starting with emergency medical supply chains and expanding into energy, commodities, capital and technology.",
      "Since then the journey has compounded: a $20M fund launch in 2023, the 1GW solar rollout beginning 2025, and the Pan-African EV Network targeted for 2027. We call ourselves a movement because our mission — elevating Africa's industrial and economic destiny — belongs to everyone who joins it."
    ],
    highlights: [
      { t: '2020 — Founding', d: 'Born during COVID-19 to solve African supply crises' },
      { t: '2023 — $20M Fund', d: 'Major capital-raising milestone unlocking growth' },
      { t: '2025 — 1GW Solar', d: 'Massive renewable deployment underway' },
      { t: '2027 — EV Network', d: 'Continental electric mobility infrastructure' }
    ],
    stats: [
      { value: '2020', label: 'Year founded' },
      { value: '$50M+', label: 'Capital commitments in pipeline' },
      { value: '35%', label: 'Annual growth target through 2027' }
    ],
    initiative: { title: 'Ascende Superius — Seek Higher Things', text: 'Our motto unites three languages and one mission: Excelsior (ever upward), Exousia (legitimate power) and Eccellenza (excellence).' },
    related: ['excelsior', 'leadership', 'un-sdgs']
  },
  'leadership': {
    title: 'Leadership Circle', category: 'Our People', accent: '#00CED1',
    tagline: 'The visionaries engineering Africa\'s sustainable ascent',
    heroImage: U('photo-1556761175-5973dc0f32e7'),
    overview: [
      "Xcelsior is governed by a board and executive team combining deep African market knowledge with global financial, legal and technological expertise. Trevor Harrison (Chairman & GCEO) leads group strategy, supported by Michael Mapfumo (Group COO) on operations and mineral value chains, and Cathrine Chimanda (Board Secretary & CLO) on governance and international trade law.",
      "The executive bench includes Debra Zoe Mugoto (CFO — DFI funding and PE structuring), Chipo Mugoto (CMO — brand and ESG communications), Takudzwa Murimi (CTO — AI, blockchain and IoT), Collins Lloyd Chimamanda (Director of Commodities — agro-value chains and traceable mining), Ignatius Daiton (Director of Logistics — solar transport and EV infrastructure) and Chido Murimi (CCO — head of Advantage Media)."
    ],
    highlights: [
      { t: 'Trevor Harrison — Chairman & GCEO', d: 'Visionary strategist in real estate development and financial structuring' },
      { t: 'Michael Mapfumo — Group COO', d: 'Operations expert in mineral development and resource value chains' },
      { t: 'Cathrine Chimanda — Board Secretary & CLO', d: 'Legal expert in international trade and corporate governance' },
      { t: 'Debra Zoe Mugoto — CFO', d: 'Finance executive specialising in DFI funding and PE structuring' },
      { t: 'Takudzwa Murimi — CTO', d: 'Deploying AI, blockchain and IoT solutions for African markets' },
      { t: 'Collins Lloyd Chimamanda — Director of Commodities', d: 'Agro-value chain development and blockchain-traceable mining' }
    ],
    stats: [
      { value: '9+', label: 'Senior leaders' },
      { value: '6', label: 'Disciplines: finance, law, ops, tech, trade, media' },
      { value: '3', label: 'Countries of active leadership presence' }
    ],
    initiative: { title: 'Governance First', text: 'A formal board with non-executive directors and a Chief Legal Officer ensures every Xcelsior venture is accountable, compliant and built to last.' },
    related: ['our-story', 'exousia', 'eccellenza']
  },
  '1gw-solar-initiative': {
    title: '1GW Solar Initiative', category: 'Flagship Project', accent: '#FF8C00',
    tagline: 'One gigawatt of clean African power — the cornerstone of our energy division',
    heroImage: U('photo-1509391366360-2e959784a276'),
    overview: [
      "The 1GW Solar Initiative is Xcelsior's cornerstone energy program: a phased rollout of utility-scale solar farms, commercial rooftop portfolios and solar microgrids totalling one gigawatt of installed capacity. It anchors Zimbabwe's energy transition and directly supports Vision 2030's power-security goals.",
      "The program blends project finance, DFI capital and PPA-backed revenue, with roughly 300MW already deployed or in construction. Every megawatt displaces diesel and load-shedding, unlocking industrial growth that unreliable grids have held back for decades."
    ],
    highlights: [
      { t: 'Utility-Scale Farms', d: 'Grid-connected solar plants with long-term PPAs' },
      { t: 'Commercial Rooftops', d: 'Behind-the-meter savings for industry' },
      { t: 'Rural Microgrids', d: 'Decentralised power for off-grid communities' },
      { t: '250K Solar Homes', d: 'Residential program bringing power to families' }
    ],
    stats: [
      { value: '1GW', label: 'Total program capacity' },
      { value: '300MW', label: 'Deployed / in construction' },
      { value: '75%', label: 'Progress to current-phase target' }
    ],
    initiative: { title: 'Defence Solar Project', text: 'A landmark public-sector partnership solarising defence installations — cutting state energy costs while proving utility-scale delivery capability.' },
    related: ['defence-solar', 'solar-fund', 'ev-network']
  },
  'ai-logistics': {
    title: 'AI Logistics — XAINE', category: 'Technology', accent: '#00CED1',
    tagline: 'AI-driven analytics solving national-scale logistics, agriculture and energy challenges',
    heroImage: U('photo-1555255707-c07966088b7b'),
    overview: [
      "XAINE is Xcelsior's artificial intelligence division, building AI-driven analytics for national-scale challenges in agriculture, logistics and energy. Its logistics models optimise routing, fleet utilisation and demand forecasting across our commodity corridors — cutting cost-per-tonne and emissions simultaneously.",
      "Developed by Crossfyr Technology, our R&D arm, XAINE adapts frontier AI to African data realities: sparse connectivity, informal markets and multi-modal transport — turning them from obstacles into modelled variables."
    ],
    highlights: [
      { t: 'Route Optimisation', d: 'Dynamic routing across road, rail and port corridors' },
      { t: 'Demand Forecasting', d: 'Predictive models for commodity flows and energy load' },
      { t: 'Fleet Intelligence', d: 'Utilisation, maintenance and EV-transition analytics' },
      { t: 'Digital Public Infrastructure', d: 'National-scale integrations with government systems' }
    ],
    stats: [
      { value: '3', label: 'Sectors modelled: agri, logistics, energy' },
      { value: '20%+', label: 'Targeted cost-per-tonne reduction' },
      { value: '24/7', label: 'Real-time corridor monitoring' }
    ],
    initiative: { title: 'Crossfyr Technology', text: 'Our core R&D house identifies, adapts and industrialises global technologies — AI, blockchain, IoT — for Africa\'s unique context.' },
    related: ['blockchain-traceability', 'ev-network', 'communication-systems']
  },
  'un-sdgs': {
    title: '10 UN SDGs Advanced', category: 'Strategic Alignment', accent: '#00CED1',
    tagline: 'Direct, measurable impact on ten Sustainable Development Goals',
    heroImage: U('photo-1509062522246-3755977927d7'),
    overview: [
      "Xcelsior's portfolio delivers direct, measurable impact on ten of the seventeen UN Sustainable Development Goals — most notably SDG 7 (Affordable & Clean Energy), SDG 8 (Decent Work & Economic Growth), SDG 9 (Industry, Innovation & Infrastructure) and SDG 11 (Sustainable Cities & Communities).",
      "Impact is not a side effect; it is engineered into deal structure. Solar projects carry energy-access covenants, agriculture programs embed smallholder inclusion, and every fund reports against SDG-mapped KPIs."
    ],
    highlights: [
      { t: 'SDG 7 — Clean Energy', d: '1GW solar program and rural microgrids' },
      { t: 'SDG 8 — Decent Work', d: 'Veteran plots, youth skills and formalised mining' },
      { t: 'SDG 9 — Infrastructure', d: 'EV corridors, logistics and industrial parks' },
      { t: 'SDG 11 — Sustainable Cities', d: 'Climate-resilient smart urban development' }
    ],
    stats: [
      { value: '10', label: 'SDGs directly advanced' },
      { value: '78%', label: 'Livelihoods-improved metric' },
      { value: '25%', label: 'Carbon emissions reduction' }
    ],
    initiative: { title: 'SDG-Mapped Reporting', text: 'Every division reports quarterly against SDG-mapped KPIs — making impact as auditable as revenue.' },
    related: ['zimbabwe-vision-2030', 'au-agenda-2063', 'esg-standard']
  },
  'ev-network': {
    title: 'Pan-African EV Network', category: 'Flagship Project', accent: '#FF8C00',
    tagline: "Building the continent's leading electric vehicle charging corridors",
    heroImage: U('photo-1593941707882-a5bba14938c7'),
    overview: [
      "The Pan-African EV Network is Xcelsior's boldest infrastructure play: a continental web of solar-powered EV charging corridors, beginning with the Harare–Johannesburg and Harare–Beira trade routes and expanding along Africa's key logistics arteries by 2027.",
      "Each charging node pairs fast-charging infrastructure with solar generation and battery storage — making the network largely grid-independent. Our logistics division simultaneously transitions commercial fleets to electric, guaranteeing anchor demand from day one."
    ],
    highlights: [
      { t: 'Solar-Powered Nodes', d: 'Charging stations with dedicated PV and storage' },
      { t: 'Trade-Route First', d: 'Corridors mapped to existing freight flows' },
      { t: 'Fleet Transition', d: 'Anchor demand from electrified logistics fleets' },
      { t: 'Mine-to-Battery Loop', d: 'Powered by the lithium and nickel we trade' }
    ],
    stats: [
      { value: '2027', label: 'Continental corridor target' },
      { value: '2', label: 'Launch corridors underway' },
      { value: '100%', label: 'Renewable-powered charging' }
    ],
    initiative: { title: 'EV Network Planning Advisory', text: 'Our Strategic Advisory division supports governments on EV-readiness policy, tariffs and charging standards across the region.' },
    related: ['1gw-solar-initiative', 'nickel', 'ai-logistics']
  },
  'esg-standard': {
    title: 'ESG Gold Standard', category: 'Sustainability', accent: '#00CED1',
    tagline: 'Beyond compliance — ESG engineered into every operation',
    heroImage: U('photo-1466611653911-95081537e5b7'),
    overview: [
      "Xcelsior treats ESG as an operating system, not a report. Our value chains are built to satisfy the world's most stringent frameworks — including the EU Green Deal and Carbon Border Adjustment Mechanism (CBAM) — through ethical sourcing, carbon-conscious operations and transparent, auditable data.",
      "This rigour is commercial strategy: as global buyers face carbon border taxes and due-diligence laws, CBAM-ready African supply becomes a premium product. Excellence in ESG is how African commodities win, not just comply."
    ],
    highlights: [
      { t: 'CBAM-Ready Chains', d: 'Carbon-accounted exports for EU market access' },
      { t: 'Ethical Sourcing', d: 'Human-rights due diligence across suppliers' },
      { t: 'Carbon Accounting', d: 'Measured, reduced and offset emissions' },
      { t: 'Transparent Data', d: 'Blockchain-anchored ESG evidence trails' }
    ],
    stats: [
      { value: '25%', label: 'Carbon reduction achieved' },
      { value: 'EU CBAM', label: 'Compliance-ready operations' },
      { value: '78%', label: 'Livelihoods improved (social metric)' }
    ],
    initiative: { title: 'Carbon Credit Brokerage', text: 'Our sustainability division monetises verified emission reductions — turning African climate action into a revenue stream.' },
    related: ['carbon-credits', 'un-sdgs', 'blockchain-traceability']
  },
  'veteran-empowerment': {
    title: '12,750 Veterans Empowered', category: 'Impact Program', accent: '#FF8C00',
    tagline: 'Transforming dry land into thriving solar-irrigated farms',
    heroImage: U('photo-1560493676-04071c5f467b'),
    overview: [
      "The Veteran Agricultural Empowerment program is Xcelsior Impact's flagship: 12,750 war veterans and their families settled on productive, solar-irrigated agricultural plots. What was dry, idle land now produces non-GMO maize, sunflower and horticulture for formal export value chains.",
      "Each plot receives solar-powered irrigation, climate-smart agronomy training and guaranteed market access through Jordan Crossover's blockchain-tracked supply chains — turning national service into sustainable livelihood."
    ],
    highlights: [
      { t: 'Solar Irrigation', d: 'Reliable water independent of grid or diesel' },
      { t: 'Agronomy Training', d: 'Climate-smart techniques raising yields' },
      { t: 'Guaranteed Offtake', d: 'Direct market access at fair prices' },
      { t: 'Family Prosperity', d: 'Whole households lifted into formal economy' }
    ],
    stats: [
      { value: '12,750', label: 'Veteran plots active' },
      { value: '85%', label: 'Program rollout progress' },
      { value: '$500', label: 'Funds solar irrigation for one plot' }
    ],
    initiative: { title: 'Xcelsior Impact Division', text: 'Beyond veterans, the division runs youth skills development, civic education and the WILpower women-in-leadership program.' },
    related: ['impact-philanthropy', 'non-gmo-maize', 'un-sdgs']
  },
  'zimbabwe-vision-2030': {
    title: 'Zimbabwe Vision 2030', category: 'Strategic Alignment', accent: '#00CED1',
    tagline: 'Direct contribution to an upper-middle-income Zimbabwe',
    heroImage: U('photo-1449824913935-59a10b8d2000'),
    overview: [
      "Zimbabwe's Vision 2030 targets upper-middle-income status through the NDS1 and NDS2 development strategies. Xcelsior's portfolio maps directly onto its pillars: our 1GW solar program addresses power security, veteran agriculture advances food security, and our logistics and real-estate ventures modernise infrastructure.",
      "By structuring projects that governments need and investors want, we make national strategy bankable — converting policy documents into constructed, revenue-generating assets."
    ],
    highlights: [
      { t: 'Energy Security', d: '1GW solar initiative easing national load-shedding' },
      { t: 'Food Security', d: 'Non-GMO grain and oilseed production at scale' },
      { t: 'Infrastructure', d: 'Logistics corridors, industrial parks, smart property' },
      { t: 'Export Growth', d: 'Value-added minerals and agriproducts' }
    ],
    stats: [
      { value: '2030', label: 'National vision horizon' },
      { value: 'NDS1/2', label: 'Strategies directly served' },
      { value: '4', label: 'Vision pillars addressed' }
    ],
    initiative: { title: 'Ministry of Energy Solar Framework', text: 'Our advisory casework includes national solar framework development — policy architecture for the energy transition.' },
    related: ['au-agenda-2063', 'un-sdgs', '1gw-solar-initiative']
  },
  'au-agenda-2063': {
    title: 'AU Agenda 2063', category: 'Strategic Alignment', accent: '#FF8C00',
    tagline: "Advancing the Africa We Want — sustainable cities, transport and agriculture",
    heroImage: U('photo-1480714378408-67cf0d13bc1b'),
    overview: [
      "Agenda 2063 is the African Union's blueprint for a prosperous, integrated continent. Xcelsior's ventures advance its aspirations directly: the Pan-African EV Network builds integrated transport, our smart-city real estate delivers sustainable urbanism, and climate-smart agriculture modernises food systems.",
      "We treat continental integration as an investment thesis — AfCFTA-enabled trade corridors, cross-border energy projects and regional capital pools that make 'Africa scale' the default, not the exception."
    ],
    highlights: [
      { t: 'Integrated Transport', d: 'EV corridors linking regional economies' },
      { t: 'Sustainable Cities', d: 'Climate-resilient smart urban developments' },
      { t: 'Agricultural Modernisation', d: 'Technology-led food system transformation' },
      { t: 'AfCFTA Alignment', d: 'Continental free-trade-area ready value chains' }
    ],
    stats: [
      { value: '2063', label: 'Continental vision horizon' },
      { value: '3', label: 'Aspirations directly advanced' },
      { value: '54', label: 'Markets in the AfCFTA opportunity' }
    ],
    initiative: { title: 'Pan-African by Design', text: 'Every Xcelsior venture is architected to cross borders — because Agenda 2063\'s Africa is one market, not fifty-four.' },
    related: ['zimbabwe-vision-2030', 'ev-network', 'smart-cities-reit']
  },
  'defence-solar': {
    title: 'Defence Solar Project', category: 'Energy Division', accent: '#00CED1',
    tagline: 'Solarising national defence installations — public-sector energy transition at scale',
    heroImage: U('photo-1508514177221-188b1cf16e9d'),
    overview: [
      "The Defence Solar Project is a landmark public-sector partnership deploying solar generation and storage across defence installations. It reduces the state's energy bill, hardens critical infrastructure against grid failure, and demonstrates utility-scale delivery under the most demanding procurement standards.",
      "The project anchors our 1GW Solar Initiative pipeline, proving the engineering, financing and compliance model we now replicate for other ministries and parastatals."
    ],
    highlights: [
      { t: 'Critical Resilience', d: 'Energy-independent installations immune to load-shedding' },
      { t: 'State Savings', d: 'Long-term reduction of public energy expenditure' },
      { t: 'Secure Delivery', d: 'Vetted teams meeting defence procurement standards' },
      { t: 'Replicable Model', d: 'Template for ministry-wide solar rollout' }
    ],
    stats: [
      { value: '24/7', label: 'Power resilience with storage' },
      { value: '25yr', label: 'Asset design life' },
      { value: '1GW', label: 'Program pipeline it anchors' }
    ],
    initiative: { title: 'Public Sector Solar Framework', text: 'Lessons from the defence program feed our advisory work on national solar frameworks and public-building solarisation.' },
    related: ['1gw-solar-initiative', 'security-equipment', 'solar-fund']
  },
  'blockchain-traceability': {
    title: 'Blockchain Traceability', category: 'Commodities Division', accent: '#FF8C00',
    tagline: 'Transparent, tamper-proof provenance for African commodities',
    heroImage: U('photo-1639762681485-074b7f938ba0'),
    overview: [
      "Global buyers increasingly demand proof — of origin, of ethics, of carbon. Xcelsior's blockchain traceability platform, built by Crossfyr Technology, anchors every commodity consignment to an immutable digital record: mine or farm of origin, handlers, assays, certifications and logistics milestones.",
      "The result is African supply that clears due-diligence checks other origins fail — commanding premiums in EU and North American markets where CBAM and supply-chain laws bite."
    ],
    highlights: [
      { t: 'Immutable Records', d: 'Tamper-proof provenance from source to buyer' },
      { t: 'Assay Anchoring', d: 'Lab results and certifications on-chain' },
      { t: 'Due-Diligence Ready', d: 'Compliance packs generated automatically' },
      { t: 'Premium Access', d: 'Unlocking regulated high-value markets' }
    ],
    stats: [
      { value: '100%', label: 'Consignments traceable' },
      { value: '2', label: 'Chains live: minerals & agriproducts' },
      { value: 'EU', label: 'CBAM & CSDDD compliance target' }
    ],
    initiative: { title: 'Crystal Clear + Jordan Crossover', text: 'Our mineral and agriculture trading houses run every export through the platform — transparency as a standard feature, not an upgrade.' },
    related: ['lithium', 'non-gmo-maize', 'esg-standard']
  },
  'visionaries-rise': {
    title: 'Visionaries Rise Accelerator', category: 'VC/PE Division', accent: '#00CED1',
    tagline: "Funding and building Africa's next generation of sustainable startups",
    heroImage: U('photo-1531482615713-2afd69097998'),
    overview: [
      "Visionaries Rise is the accelerator program of VisionariZ, our early-stage venture arm. It invests in African founders building cleantech, fintech, agritech and healthtech ventures — pairing capital with the Xcelsior group's infrastructure, distribution and regulatory muscle.",
      "Portfolio companies plug directly into our operations: an agritech startup pilots on 12,750 veteran plots; an energy-fintech deploys across our solar customer base. We compress the distance between seed round and real revenue."
    ],
    highlights: [
      { t: 'Seed to Series A', d: 'Cheques from $50K with follow-on capacity' },
      { t: 'Venture Studio Support', d: 'Group infrastructure as startup springboard' },
      { t: 'Sector Focus', d: 'Cleantech, fintech, agritech, healthtech' },
      { t: 'RyZ Capital Pathway', d: 'Growth-stage capital for graduating ventures' }
    ],
    stats: [
      { value: '$20M', label: 'Rise Scale-Up Fund' },
      { value: '4', label: 'Focus sectors' },
      { value: '22%', label: 'Target IRR (AgriTech VC Fund)' }
    ],
    initiative: { title: 'VisionariZ & RyZ Capital', text: 'Twin financial catalysts: VisionariZ funds the audacious early bets; RyZ Capital scales the proven winners.' },
    related: ['agritech-vc-fund', 'ai-logistics', 'our-story']
  },
  'telemedicine-hubs': {
    title: 'Telemedicine Hubs', category: 'Health Division', accent: '#FF8C00',
    tagline: 'Connecting rural patients to specialists through technology',
    heroImage: U('photo-1622253692010-333f2da6031d'),
    overview: [
      "Xcelsior's Health division deploys telemedicine hubs — connected clinics equipping rural communities with diagnostics, connectivity and trained health workers linked to specialist physicians in cities and abroad.",
      "Each hub combines point-of-care diagnostics, satellite or fibre connectivity and AI-assisted triage from XAINE, multiplying the reach of every scarce doctor. A $5,000 sponsorship fully equips one clinic."
    ],
    highlights: [
      { t: 'Specialist Access', d: 'Video consultations from village to city hospital' },
      { t: 'Point-of-Care Diagnostics', d: 'On-site testing with instant results' },
      { t: 'AI Triage', d: 'Smart screening prioritising urgent cases' },
      { t: 'Local Health Workers', d: 'Trained community staff running each hub' }
    ],
    stats: [
      { value: '$5,000', label: 'Equips one full clinic' },
      { value: '15min', label: 'Rapid diagnostic turnaround' },
      { value: '24/7', label: 'Specialist network availability' }
    ],
    initiative: { title: 'Zoe Life Integration', text: 'Hubs double as distribution points for Zoe Life wellness products and national health campaigns.' },
    related: ['diagnostic-tools', 'medical-equipment', 'impact-philanthropy']
  },
  'carbon-credits': {
    title: 'Carbon Credit Brokerage', category: 'Sustainability Division', accent: '#00CED1',
    tagline: 'Turning verified African climate action into a revenue stream',
    heroImage: U('photo-1466611653911-95081537e5b7'),
    overview: [
      "Africa generates some of the world's highest-integrity carbon reduction opportunities — yet captures a fraction of carbon-market value. Xcelsior's brokerage originates, verifies and trades carbon credits from our own portfolio (solar, clean cooking, agroforestry) and from vetted third-party projects.",
      "We manage the full cycle: baseline studies, registry certification (Verra/Gold Standard), issuance and placement with corporate buyers — ensuring African projects are paid global prices for real, additional impact."
    ],
    highlights: [
      { t: 'Origination', d: 'Solar, cookstove and agroforestry credit projects' },
      { t: 'Certification', d: 'Verra and Gold Standard registry pathways' },
      { t: 'Corporate Placement', d: 'Direct sales to net-zero-committed buyers' },
      { t: 'Revenue Share', d: 'Communities paid for verified stewardship' }
    ],
    stats: [
      { value: '25%', label: 'Group carbon reduction achieved' },
      { value: '2', label: 'Registries: Verra, Gold Standard' },
      { value: '3', label: 'Project types originated' }
    ],
    initiative: { title: 'ESG Integration Mandate', text: 'The Sustainability division embeds environmental responsibility across all group operations — carbon is measured before it is monetised.' },
    related: ['esg-standard', '1gw-solar-initiative', 'un-sdgs']
  },
  'solar-fund': {
    title: 'Solar Fund', category: 'Investment Opportunity', accent: '#00CED1',
    tagline: '500MW of renewable infrastructure targeting 18% IRR',
    heroImage: U('photo-1509391366360-2e959784a276'),
    overview: [
      "The Xcelsior Solar Fund finances 500MW of the group's 1GW solar pipeline: utility-scale plants and commercial portfolios backed by long-term power purchase agreements with creditworthy offtakers.",
      "Targeting an 18% IRR, the fund offers investors hard-asset exposure to Africa's most acute infrastructure gap — energy — with revenue contracted in USD-linked terms and construction executed by proven EPC partners."
    ],
    highlights: [
      { t: 'PPA-Backed Revenue', d: 'Long-term contracted cashflows' },
      { t: 'Proven Pipeline', d: 'Sites, licences and offtakers secured' },
      { t: 'Hard-Asset Security', d: 'Infrastructure collateral underpinning returns' },
      { t: 'Impact Reporting', d: 'SDG 7 metrics with every distribution' }
    ],
    stats: [
      { value: '500MW', label: 'Fund capacity target' },
      { value: '18%', label: 'Projected IRR' },
      { value: 'Active', label: 'Fund status — open to investors' }
    ],
    initiative: { title: 'Part of the 1GW Initiative', text: 'The fund is the primary financing vehicle for the group\'s flagship gigawatt-scale solar program.' },
    related: ['1gw-solar-initiative', 'smart-cities-reit', 'agritech-vc-fund']
  },
  'smart-cities-reit': {
    title: 'Smart Cities REIT', category: 'Investment Opportunity', accent: '#FF8C00',
    tagline: 'Climate-resilient urban development targeting 15% IRR',
    heroImage: U('photo-1480714378408-67cf0d13bc1b'),
    overview: [
      "The Smart Cities REIT assembles a multi-project portfolio of sustainable, climate-resilient real estate: smart residential estates, green industrial parks and mixed-use developments engineered by Xcelsior Real Estate.",
      "Each asset integrates solar generation, water recycling, EV charging and digital infrastructure from day one — commanding premium rents and future-proof valuations while targeting a 15% IRR for investors."
    ],
    highlights: [
      { t: 'Green by Design', d: 'Solar, water recycling and smart systems standard' },
      { t: 'Industrial Parks', d: 'ESG-compliant manufacturing and logistics space' },
      { t: 'Mixed-Use Estates', d: 'Live-work-play developments for growing cities' },
      { t: 'REIT Structure', d: 'Liquidity and distributions in a regulated wrapper' }
    ],
    stats: [
      { value: '15%', label: 'Projected IRR' },
      { value: 'Multi', label: 'Project pipeline stage' },
      { value: '100%', label: 'Assets with on-site renewables' }
    ],
    initiative: { title: 'Agenda 2063 Cities', text: 'The REIT operationalises the AU\'s sustainable-cities aspiration — profitable proof that African urbanisation can be green.' },
    related: ['au-agenda-2063', 'solar-fund', 'agritech-vc-fund']
  },
  'agritech-vc-fund': {
    title: 'AgriTech VC Fund', category: 'Investment Opportunity', accent: '#00CED1',
    tagline: '$10M for climate-smart agriculture startups targeting 22% IRR',
    heroImage: U('photo-1625246333195-78d9c38ad449'),
    overview: [
      "The $10M AgriTech VC Fund backs climate-smart agriculture startups across Southern and Eastern Africa: precision farming, solar irrigation tech, post-harvest logistics, agri-fintech and market-access platforms.",
      "Managed through VisionariZ, the fund de-risks early-stage bets with a unique advantage — portfolio companies pilot directly on Xcelsior's 12,750 veteran plots and inside our commodity value chains, compressing time to product-market fit. Target IRR: 22%."
    ],
    highlights: [
      { t: 'Built-In Pilot Ground', d: '12,750 plots as a living laboratory' },
      { t: 'Value-Chain Access', d: 'Instant distribution through group trading arms' },
      { t: 'Climate-Smart Thesis', d: 'Every investment hardens food systems' },
      { t: 'Hands-On Support', d: 'Visionaries Rise accelerator resources' }
    ],
    stats: [
      { value: '$10M', label: 'Fund size' },
      { value: '22%', label: 'Projected IRR' },
      { value: 'Active', label: 'Fund status — open to investors' }
    ],
    initiative: { title: 'Visionaries Rise Pipeline', text: 'The accelerator feeds the fund a pre-vetted dealflow of founders already proven inside the Xcelsior ecosystem.' },
    related: ['visionaries-rise', 'veteran-empowerment', 'solar-fund']
  },
  'impact-philanthropy': {
    title: 'Impact Philanthropy', category: 'Xcelsior Impact', accent: '#FF8C00',
    tagline: 'Strategic giving that transforms lives — measurably',
    heroImage: U('photo-1524594081293-190a2fe0baae'),
    overview: [
      "Xcelsior Impact channels philanthropic capital into programs with engineered, measurable outcomes. A $500 gift installs solar irrigation on one veteran's plot — converting dry land into perpetual income. A $5,000 gift fully equips a telemedicine clinic serving thousands.",
      "Beyond these flagships, the division runs WILpower (Women in Leadership and Power), youth skills development, and the 'I Am Zimbabwe' and 'We the People' civic education programs — building the human infrastructure that hard infrastructure needs."
    ],
    highlights: [
      { t: '$500 — Solar Irrigation', d: 'One veteran plot converted to productive farmland' },
      { t: '$5,000 — Telemedicine Clinic', d: 'Full equipment for one connected rural clinic' },
      { t: 'WILpower', d: 'Women in Leadership and Power development program' },
      { t: 'Civic Education', d: "'I Am Zimbabwe' and 'We the People' initiatives" }
    ],
    stats: [
      { value: '12,750', label: 'Veterans already empowered' },
      { value: '100%', label: 'Of gifts deployed to programs' },
      { value: '4', label: 'Flagship impact programs' }
    ],
    initiative: { title: 'Give Through Us', text: 'Contact strategy@xsi.co.zw to structure a gift, sponsorship or corporate social investment partnership.' },
    related: ['veteran-empowerment', 'telemedicine-hubs', 'un-sdgs']
  }
};

export const TOPICS = { ...CORE_TOPICS, ...COMMODITY_TOPICS };

export const slugify = (label) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export const getTopic = (slug) => TOPICS[slug] || null;
